/**
 * The scrollable box `element` lives in — the planner's column, the month sidebar, or the page.
 */
export function scrollParentOf(element: HTMLElement): HTMLElement {
  let declared: HTMLElement | null = null
  for (let node = element.parentElement; node; node = node.parentElement) {
    const overflowY = getComputedStyle(node).overflowY
    if (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") {
      if (node.scrollHeight > node.clientHeight) {
        // A box that overflows right now wins, since an `overflow: auto` wrapper sized by its own content
        // never scrolls and would swallow corrections meant for the real scroller outside it.
        return node
      }
      declared ??= node
    }
  }
  return declared ?? (document.scrollingElement as HTMLElement | null) ?? document.documentElement
}

/**
 * The height a slot has to clear to sit at the top of `scroller` — the scroller's own sticky
 * header, measured rather than hardcoded so it tracks the header's CSS instead of drifting from it.
 */
export function stickyInsetOf(scroller: HTMLElement): number {
  for (const child of scroller.children) {
    if (getComputedStyle(child).position === "sticky") {
      return child.getBoundingClientRect().height
    }
  }
  return 0
}

/**
 * Keeps `slot` on the viewport line `lineOf` reports, correcting it every time the planner reflows
 * around it, until the user takes over.
 *
 * A hold, not a one-shot scroll: lessons mount as one React root each and expand over ~200ms, so
 * the ones above this slot push it far down the page well after any single scroll has run — the
 * browser's own scroll anchoring does not survive that reflow.
 *
 * The hold is driven by observation, never by a clock. Timing it would mean guessing how long the
 * planner takes to settle, and it settles at wildly different speeds depending on the machine. Any
 * quiet-period guess tuned on a fast laptop releases mid-reflow on a slow one. So the hold reacts
 * to the resizes themselves and ends on the only unambiguous signal there is — the user taking over.
 *
 * `lineOf` is read on every correction, not captured, so a caller whose target line depends on
 * layout (the top of a scroller, say) stays correct as that layout moves.
 *
 * Returns a teardown. It stops observing but does NOT call `onRelease`: only the user ends the hold
 * for good, and an island that unmounts mid-reflow may well remount and pick the hold straight up.
 */
export function holdSlotOnLine(
  slot: HTMLElement,
  lineOf: () => number,
  onRelease: () => void,
): () => void {
  const scroller = scrollParentOf(slot)

  // Put the slot back on its line. A no-op once nothing is moving, so this is safe to run on
  // every resize the column reports.
  const holdTheLine = (): void => {
    const drift = slot.getBoundingClientRect().top - lineOf()
    if (Math.abs(drift) >= 1) scroller.scrollTop += drift
  }

  // One correction is not always enough: a resize can settle over more than one layout pass (the
  // browser's own scroll anchoring also moves the offset, and our scroll can change which content
  // is laid out), so re-apply on the next frame and let that chase the remainder to zero. Bounded
  // by the drift itself, not by a clock — it stops as soon as there is none.
  let queued = 0
  const holdUntilStill = (): void => {
    holdTheLine()
    cancelAnimationFrame(queued)
    queued = requestAnimationFrame(() => {
      const before = slot.getBoundingClientRect().top
      holdTheLine()
      if (Math.abs(slot.getBoundingClientRect().top - before) >= 1) holdUntilStill()
    })
  }

  // ResizeObserver has no subtree mode, and neither the scroller nor its direct children
  // resize — in the week view the slots sit a dozen grid wrappers deep. What shoves this slot
  // is its siblings and its ancestors' siblings at every level up to the scroller.
  const sizes = new ResizeObserver(holdUntilStill)
  const watchMovers = (): void => {
    for (
      let node: HTMLElement | null = slot;
      node !== null && node !== scroller;
      node = node.parentElement
    ) {
      for (const sibling of node.parentElement?.children ?? []) sizes.observe(sibling)
    }
    for (const child of scroller.children) sizes.observe(child)
  }
  watchMovers()
  // One rewalk per frame: a node observed a frame late still fires ResizeObserver's initial
  // callback, and corrections are absolute, so nothing is missed.
  let rewatchQueued = 0
  const children = new MutationObserver(() => {
    cancelAnimationFrame(rewatchQueued)
    rewatchQueued = requestAnimationFrame(watchMovers)
  })
  children.observe(scroller, { childList: true, subtree: true })

  const stopObserving = (): void => {
    sizes.disconnect()
    children.disconnect()
    cancelAnimationFrame(queued)
    cancelAnimationFrame(rewatchQueued)
  }

  // The user taking over ends the hold — any input, not just scrolling. Once they have clicked
  // into a lesson or scrolled away, dragging the viewport back to a line they have deliberately
  // left is worse than letting go. Releasing removes these listeners too: the hold is over, so
  // every later keystroke re-running the release would be waste (and repeat `onRelease`).
  const userInput = ["wheel", "touchstart", "pointerdown", "keydown"] as const
  const removeInputListeners = (): void => {
    for (const type of userInput) {
      window.removeEventListener(type, release, { capture: true })
    }
  }
  function release(): void {
    stopObserving()
    removeInputListeners()
    onRelease()
  }
  for (const type of userInput) {
    window.addEventListener(type, release, { passive: true, capture: true })
  }

  return () => {
    stopObserving()
    removeInputListeners()
  }
}
